// SignupPage — Shopify-style 3-step onboarding.
// Step 1: Email → request OTP → verify OTP (2 phase trong cùng step 1, vẫn 3 bước)
// Step 2: Password + Tên hiển thị + Phone (OTP verify bắt buộc)
// Step 3: Tên doanh nghiệp (workspace)
// Submit cuối: POST /onboarding/signup → auto-login → /inbox.
//
// UX smooth: bấm "Tiếp tục" ở step 1 → UI transition NGAY sang phase verify
// (không chờ API), email OTP request fire-and-forget. BE cũng setImmediate gửi
// SMTP (response ~50ms, không chờ delivery). Nếu API hard-fail (rate limit,
// email_exists), rollback UI về phase enter + toast lỗi.

import { useState, useRef, useEffect } from 'react';
import { useNavigate, Navigate, Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import { Trans, useLingui } from '@lingui/react/macro';
import { useAuthStore } from '../lib/authStore';
import { getLocale } from '@cnv/ui/i18n';
import { ArrowRight, ArrowLeft, Mail, Lock, User, Phone, Building2, Loader2, Eye, EyeOff, CheckCircle2, ShieldCheck } from 'lucide-react';
import '../i18n-host';

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

const STEPS = [
  { num: 1, key: 'email',    label: 'Email' },
  { num: 2, key: 'account',  label: 'Tài khoản' },
  { num: 3, key: 'business', label: 'Doanh nghiệp' },
];

export default function SignupPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [searchParams, setSearchParams] = useSearchParams();
  // KHÔNG dùng useAuthStore selector cho isAuthenticated — sẽ subscribe + re-render
  // ngay khi setSession() chạy → guard <Navigate to="/inbox"> fire TRƯỚC khi
  // navigate('/setting-up') kịp thực thi → user miss splash.
  // Đọc 1 lần ở mount (getState), không subscribe:
  const isAuthenticatedOnMount = useRef(!!useAuthStore.getState().accessToken).current;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ email: '', password: '', display_name: '', org_name: '', phone: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Máy chủ từ chối MẬT KHẨU lúc «Tạo workspace» (luật chỉ máy chủ biết đủ: danh sách mật khẩu phổ biến…) ⇒ nhớ
  // { password, message } để kéo về bước 2 và hiện câu đó dưới ô; đổi mật khẩu là câu tự tắt.
  const [pwdRejected, setPwdRejected] = useState(null);

  // OTP state — phone OTP ở step 2, email OTP ở step 1.
  const [otpCode, setOtpCode] = useState('');
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpRequesting, setOtpRequesting] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const otpTriedRef = useRef('');   // mã đã gửi đi verify — chặn auto-verify lặp (xem effect dưới)

  // Email OTP state — step 1 chia 2 phase: 'enter' → 'verify'.
  // emailVerified true → cho phép sang step 2. KHÔNG reset khi back từ step 2,3
  // vì verified flag BE có TTL 30 phút.
  const [emailPhase, setEmailPhase] = useState('enter');           // 'enter' | 'verify'
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpVerifying, setEmailOtpVerifying] = useState(false);
  const [emailOtpRequesting, setEmailOtpRequesting] = useState(false);
  const [emailOtpCountdown, setEmailOtpCountdown] = useState(0);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailSendStatus, setEmailSendStatus] = useState(null);    // null | 'sending' | 'sent' | 'failed'

  useEffect(() => {
    if (otpCountdown <= 0) return;
    const t = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCountdown]);

  // TC-225: Browser back ở step 2/3 đáng lẽ phải quay về step trước (giữ email/data),
  // chứ không exit hẳn flow /signup. Push history state mỗi khi sang step mới + listen
  // popstate để intercept Back.
  const stepRef = useRef(step);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => {
    // Đẩy 1 history entry khi mount để có chỗ "pop" về thay vì rời /signup.
    if (typeof window !== 'undefined') {
      window.history.replaceState({ signupStep: 1 }, '');
    }
    const onPopState = (e) => {
      const targetStep = e.state?.signupStep;
      const current = stepRef.current;
      if (typeof targetStep === 'number' && targetStep >= 1 && targetStep <= 3) {
        // Browser back/forward gọi popstate — đồng bộ React state với history.
        // Nếu đang ở step 1 phase 'verify' và pop về step 1 → fall back về phase 'enter'.
        setStep(targetStep);
        if (targetStep === 1) {
          setEmailPhase((p) => (p === 'verify' && !emailVerified ? 'enter' : p));
          setEmailOtpCode('');
        }
      } else if (current > 1) {
        // Pop xuống history entry không có signupStep → giả lập "back" về step trước
        // thay vì để browser exit /signup. Tự push lại 1 entry để giữ user trong flow.
        const prev = Math.max(1, current - 1);
        setStep(prev);
        if (prev === 1) {
          setEmailPhase((p) => (p === 'verify' && !emailVerified ? 'enter' : p));
        }
        window.history.pushState({ signupStep: prev }, '');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Khi step thay đổi từ logic UI → push history entry tương ứng để Back của browser
  // sẽ pop về step trước. Không push khi step về 1 (init), không push khi popstate đang
  // sync (handled riêng).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cur = window.history.state?.signupStep;
    if (cur !== step) {
      window.history.pushState({ signupStep: step }, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  useEffect(() => {
    if (emailOtpCountdown <= 0) return;
    const t = setTimeout(() => setEmailOtpCountdown(emailOtpCountdown - 1), 1000);
    return () => clearTimeout(t);
  }, [emailOtpCountdown]);

  // Magic link: nếu URL có ?email=&otp= → auto-verify ngầm.
  // 1. Pre-fill email + OTP vào state
  // 2. Set phase 'verify' + sendStatus 'sent' (vì email đã trong inbox user)
  // 3. Gọi verify-otp NGẦM, success → advance step 2 luôn
  // 4. Strip query param khỏi URL để user refresh ko replay (history.replaceState
  //    qua setSearchParams({}))
  // Skip nếu user đã authenticated hoặc đã verify trước đó.
  const autoVerifyRanRef = useRef(false);
  useEffect(() => {
    if (autoVerifyRanRef.current) return;
    if (isAuthenticatedOnMount) return;
    const qEmail = searchParams.get('email');
    const qOtp = searchParams.get('otp');
    if (!qEmail || !qOtp) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(qEmail)) return;
    if (!/^\d{6}$/.test(qOtp)) return;
    autoVerifyRanRef.current = true;

    // Pre-fill state
    setForm((f) => ({ ...f, email: qEmail }));
    setEmailPhase('verify');
    setEmailOtpCode(qOtp);
    setEmailSendStatus('sent');

    // Verify ngầm (async, không await)
    (async () => {
      setEmailOtpVerifying(true);
      try {
        await axios.post(`${baseURL}/onboarding/email/verify-otp`, { email: qEmail, otp: qOtp });
        setEmailVerified(true);
        toast.success('Đã xác minh email');
        setStep(2);
      } catch (err) {
        const issue = err.response?.data?.error?.details?.issue;
        if (issue === 'otp_mismatch' || issue === 'otp_expired_or_not_requested') {
          toast.error('Link xác minh đã hết hạn — vui lòng yêu cầu mã mới');
        } else if (err.response?.data?.error?.code === 'rate_limited') {
          toast.error('Quá nhiều lần thử — vui lòng chờ vài phút');
        } else {
          toast.error('Xác minh thất bại — nhập lại mã hoặc gửi lại email');
        }
      } finally {
        setEmailOtpVerifying(false);
        // Strip query param khỏi URL để bảo mật (otp ko nằm trong history/refresh)
        setSearchParams({}, { replace: true });
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Autofill "one-time-code" của iOS/Android đổ 6 số vào ô OTP nhưng KHÔNG bấm hộ
  // nút "Xác minh" → otpVerified vẫn false → nút "Tiếp tục" khoá vĩnh viễn (user
  // báo 2026-08-13). Đủ 6 số là tự verify; ref chặn bắn lại cùng 1 mã khi mã sai,
  // nút "Xác minh" vẫn còn để user retry thủ công.
  // Hook phải đứng TRƯỚC early return <Navigate> dưới (rules-of-hooks). verifyOtp
  // khai báo bên dưới — an toàn vì body effect chỉ chạy sau render.
  useEffect(() => {
    if (!otpRequested || otpVerified || otpVerifying) return;
    if (!/^\d{6}$/.test(otpCode) || otpTriedRef.current === otpCode) return;
    otpTriedRef.current = otpCode;
    verifyOtp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpCode, otpRequested, otpVerified, otpVerifying]);

  if (isAuthenticatedOnMount) return <Navigate to="/inbox" replace />;

  const update = (field) => (e) => {
    const v = e.target.value;
    // Đổi phone → invalidate verified state, force user verify lại
    if (field === 'phone') {
      setOtpVerified(false);
      setOtpRequested(false);
      setOtpCode('');
      setOtpCountdown(0);
    }
    // Đổi email → invalidate cả phase + verified, kéo về 'enter'
    if (field === 'email') {
      setEmailPhase('enter');
      setEmailVerified(false);
      setEmailOtpCode('');
      setEmailOtpCountdown(0);
      setEmailSendStatus(null);
    }
    setForm({ ...form, [field]: v });
  };

  // TC-223: Email phải pass regex VÀ trong giới hạn RFC 5321 (254 chars). Trước đây chỉ
  // check regex → email 250 ký tự pass FE → BE reject 400 nhưng FE đã transition sang phase
  // 'verify' (optimistic) → vỡ UI hiện chuỗi email tràn card form.
  const EMAIL_MAX = 254;
  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && form.email.length <= EMAIL_MAX;
  // Mật khẩu ≥ 10 — người đăng ký là CHỦ workspace = quản trị ⇒ luật quản trị của kernel passwordPolicy (BE
  // onboardingService.signup → passwordRules, 20/10/2026). Máy chủ còn chặn mật khẩu quá phổ biến / chứa tên email.
  // Nút "Tiếp tục" ở step 2 khoá theo cờ này + otpVerified. Cả 2 lý do khoá PHẢI được
  // nói ra bằng chữ trên UI: user báo 14/08 "đăng ký đứng yên" — mật khẩu 7 ký tự,
  // SĐT đã xác minh, nút xám câm không giải thích gì, placeholder "Tối thiểu 8 ký tự"
  // thì đã bị chính giá trị đang nhập che mất.
  const PWD_MIN = 10;
  const pwdTooShort = form.password.length < PWD_MIN;
  // Phần tên email (trước @, từ 4 ký tự) nằm trong mật khẩu ⇒ máy chủ trả `contains_email` (kernel passwordPolicy
  // emailLocalPart). Email đã có từ bước 1 nên báo NGAY ở bước 2 — trước đây chỉ máy chủ chặn, lúc bấm «Tạo workspace»
  // ở bước 3, còn trang in «bad input» (hdslaw.vn 27/10/2026: bấm 14 lần rồi bỏ cuộc).
  const emailLocal = form.email.trim().toLowerCase().split('@')[0] || '';
  const pwdHasEmail = emailLocal.length >= 4 && form.password.toLowerCase().includes(emailLocal);
  const pwdServerMsg = pwdRejected && pwdRejected.password === form.password ? pwdRejected.message : null;
  const pwdProblem = pwdHasEmail ? t`Mật khẩu không được chứa tên email của bạn.` : pwdServerMsg;
  const isPwdValid = !pwdTooShort && !pwdProblem;
  const isOrgValid = form.org_name.trim().length >= 2;
  // VN phone: 10 số bắt đầu bằng 0, hoặc 11 số bắt đầu 84 (BE normalize cả 2)
  const phoneDigits = form.phone.replace(/\D/g, '');
  const isPhoneValid = /^0\d{9}$/.test(phoneDigits) || /^84\d{9}$/.test(phoneDigits);
  const isOtpValid = /^\d{6}$/.test(otpCode);
  const isEmailOtpValid = /^\d{6}$/.test(emailOtpCode);

  // Step 1 email OTP request — UX smooth: transition UI ngay (optimistic),
  // fire API ngầm; chỉ rollback nếu API hard-fail.
  const requestEmailOtp = async ({ silent = false } = {}) => {
    if (!isEmailValid) { toast.error('Email không hợp lệ'); return; }
    // TC-224: nếu offline thì KHÔNG transition optimistic — quan trọng vì user mất mạng
    // sẽ thấy UI tự nhảy sang OTP verify dù request chưa từng gửi. Check navigator.onLine
    // trước khi await; nếu offline → toast + giữ phase 'enter'.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      toast.error('Mất kết nối mạng — không gửi được mã. Kiểm tra mạng và thử lại.');
      return;
    }
    // Optimistic: transition phase + countdown trước khi await
    setEmailPhase('verify');
    setEmailOtpCountdown(60);
    setEmailSendStatus('sending');
    setEmailOtpRequesting(true);
    try {
      await axios.post(`${baseURL}/onboarding/email/request-otp`, { email: form.email.trim() });
      setEmailSendStatus('sent');
      if (!silent) toast.success('Đã gửi mã xác minh đến ' + form.email);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      const issue = err.response?.data?.error?.details?.issue;
      // Network error (offline, DNS fail, server down) — không có err.response.
      // TC-224: rollback hẳn về phase 'enter' để user không tưởng đã gửi được.
      if (!err.response) {
        setEmailPhase('enter');
        setEmailOtpCountdown(0);
        setEmailSendStatus(null);
        toast.error('Mất kết nối — không gửi được mã. Kiểm tra mạng và thử lại.');
      } else if (code === 'email_exists') {
        setEmailPhase('enter');
        setEmailOtpCountdown(0);
        setEmailSendStatus(null);
        toast.error('Email đã đăng ký — dùng email khác hoặc đăng nhập');
      } else if (code === 'rate_limited') {
        setEmailPhase('enter');
        setEmailOtpCountdown(0);
        setEmailSendStatus(null);
        const retry = err.response?.data?.error?.details?.retry_after || 600;
        toast.error(`Đã yêu cầu OTP quá nhiều — thử lại sau ${Math.ceil(retry / 60)} phút`);
      } else if (issue === 'invalid_format' || code === 'bad_input') {
        setEmailPhase('enter');
        setEmailOtpCountdown(0);
        setEmailSendStatus(null);
        toast.error('Email không hợp lệ');
      } else {
        // Soft fail (network glitch) — giữ phase verify, hiện trạng thái failed
        // để user thấy nút "Gửi lại" và retry
        setEmailSendStatus('failed');
        toast.error('Không gửi được email — bấm "Gửi lại"');
      }
    } finally {
      setEmailOtpRequesting(false);
    }
  };

  const verifyEmailOtp = async () => {
    if (!isEmailOtpValid) { toast.error('Mã OTP phải là 6 chữ số'); return; }
    setEmailOtpVerifying(true);
    try {
      await axios.post(`${baseURL}/onboarding/email/verify-otp`, {
        email: form.email.trim(),
        otp: emailOtpCode,
      });
      setEmailVerified(true);
      toast.success('Đã xác minh email');
      // Auto-advance step 2 — UX mượt, không cần user click thêm
      setStep(2);
    } catch (err) {
      const issue = err.response?.data?.error?.details?.issue;
      const remaining = err.response?.data?.error?.details?.remaining;
      if (issue === 'otp_mismatch') {
        toast.error(`OTP không đúng${typeof remaining === 'number' ? ` — còn ${remaining} lần thử` : ''}`);
      } else if (issue === 'otp_expired_or_not_requested') {
        toast.error('OTP đã hết hạn — bấm "Gửi lại"');
        setEmailOtpCode('');
      } else if (issue === 'too_many_attempts' || err.response?.data?.error?.code === 'rate_limited') {
        toast.error('Sai OTP quá nhiều lần — yêu cầu mã mới');
        setEmailOtpCode('');
        setEmailSendStatus(null);
      } else {
        toast.error('Xác minh thất bại — thử lại');
      }
    } finally {
      setEmailOtpVerifying(false);
    }
  };

  const requestOtp = async () => {
    if (!isPhoneValid) { toast.error('Số điện thoại không hợp lệ'); return; }
    setOtpRequesting(true);
    try {
      await axios.post(`${baseURL}/onboarding/phone/request-otp`, { phone: form.phone });
      otpTriedRef.current = '';   // mã mới → cho phép auto-verify lại
      setOtpRequested(true);
      setOtpCountdown(60);
      toast.success('Đã gửi mã OTP đến số ' + form.phone);
    } catch (err) {
      const code = err.response?.data?.error?.code;
      const issue = err.response?.data?.error?.details?.issue;
      if (code === 'phone_exists') {
        toast.error('Số điện thoại đã được đăng ký — dùng số khác hoặc đăng nhập');
      } else if (code === 'rate_limited') {
        const retry = err.response?.data?.error?.details?.retry_after || 600;
        toast.error(`Đã yêu cầu OTP quá nhiều — thử lại sau ${Math.ceil(retry / 60)} phút`);
      } else if (code === 'otp_delivery_failed') {
        toast.error('Không gửi được SMS — thử lại sau');
      } else if (issue === 'invalid_format') {
        toast.error('Số điện thoại không hợp lệ');
      } else {
        toast.error('Không gửi được OTP — thử lại');
      }
    } finally {
      setOtpRequesting(false);
    }
  };

  const verifyOtp = async () => {
    if (!isOtpValid) { toast.error('OTP phải là 6 chữ số'); return; }
    setOtpVerifying(true);
    try {
      await axios.post(`${baseURL}/onboarding/phone/verify-otp`, { phone: form.phone, otp: otpCode });
      setOtpVerified(true);
      toast.success('Đã xác minh số điện thoại');
    } catch (err) {
      const issue = err.response?.data?.error?.details?.issue;
      const remaining = err.response?.data?.error?.details?.remaining;
      if (issue === 'otp_mismatch') {
        toast.error(`OTP không đúng${typeof remaining === 'number' ? ` — còn ${remaining} lần thử` : ''}`);
      } else if (issue === 'otp_expired_or_not_requested') {
        toast.error('OTP đã hết hạn — bấm "Gửi lại OTP"');
        setOtpRequested(false);
      } else if (issue === 'too_many_attempts' || err.response?.data?.error?.code === 'rate_limited') {
        toast.error('Sai OTP quá nhiều lần — yêu cầu mã mới');
        setOtpRequested(false);
        setOtpCode('');
      } else {
        toast.error('Xác minh thất bại — thử lại');
      }
    } finally {
      setOtpVerifying(false);
    }
  };

  const next = (e) => {
    e?.preventDefault();
    if (step === 1) {
      if (!isEmailValid) { toast.error('Email không hợp lệ'); return; }
      if (emailVerified) { setStep(2); return; }
      if (emailPhase === 'enter') {
        // Trigger email OTP — UI sẽ transition optimistic
        requestEmailOtp();
      } else {
        // Đã ở phase verify → trigger verify OTP
        verifyEmailOtp();
      }
    } else if (step === 2) {
      if (pwdTooShort) { toast.error(t`Mật khẩu phải ≥ 10 ký tự`); return; }
      if (pwdProblem) { toast.error(pwdProblem, { id: 'signup-pwd' }); return; }
      if (!otpVerified) { toast.error('Vui lòng xác minh số điện thoại'); return; }
      setStep(3);
    }
  };

  const back = () => {
    // Ở step 1 phase verify → back về phase enter (giữ email)
    if (step === 1 && emailPhase === 'verify' && !emailVerified) {
      setEmailPhase('enter');
      setEmailOtpCode('');
      return;
    }
    setStep(Math.max(1, step - 1));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isOrgValid) { toast.error('Tên doanh nghiệp phải ≥ 2 ký tự'); return; }
    if (!otpVerified) { toast.error('Số điện thoại chưa được xác minh — quay lại bước 2'); setStep(2); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${baseURL}/onboarding/signup`, {
        email: form.email.trim(),
        password: form.password,
        display_name: form.display_name.trim() || form.email.split('@')[0],
        org_name: form.org_name.trim(),
        phone: form.phone,
        // Ngôn ngữ đang hiển thị (user chọn ở màn Login/Signup) → meta.users.locale ngay
        // từ INSERT, để mail chào mừng + JWT đầu tiên đã đúng tiếng.
        locale: getLocale(),
      });
      setSession({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
        activeTenant: data.active_tenant,   // FIX: BE trả `active_tenant` (không phải `tenant`) → trước đây activeTenant=undefined
      });
      toast.success(`Chào ${data.user?.display_name || data.user?.email}!`);
      // Sau signup → splash page hiển thị progress khởi tạo workspace (8 stages, ~3-5s).
      // SettingUpPage poll /onboarding/setup-status → khi completed → redirect /dashboard.
      navigate('/setting-up', { replace: true });
    } catch (err) {
      const code = err.response?.data?.error?.code;
      const details = err.response?.data?.error?.details;
      const issue = details?.issue;
      // Câu máy chủ viết cho NGƯỜI đọc (userMessage, đã dịch theo ngôn ngữ trình duyệt) nằm ở `message` top-level.
      // `error.message` là chuỗi kỹ thuật — BadInput nào cũng là 'bad input' — đừng in ra màn hình.
      const msg = err.response?.data?.message;
      if (details?.field === 'password') {
        // signup soát mật khẩu TRƯỚC khi tiêu cờ xác minh email + SĐT ⇒ quay lại bước 2 đổi mật khẩu là đi tiếp được.
        const message = msg || t`Mật khẩu không hợp lệ, vui lòng chọn mật khẩu khác.`;
        setPwdRejected({ password: form.password, message });
        toast.error(message, { id: 'signup-pwd' });
        setStep(2);
      } else if (code === 'email_taken' || code === 'email_exists') {
        toast.error('Email đã đăng ký — quay lại bước 1 hoặc đăng nhập');
        setStep(1);
      } else if (code === 'phone_exists') {
        toast.error('Số điện thoại đã được đăng ký — dùng số khác');
        setOtpVerified(false);
        setOtpRequested(false);
        setStep(2);
      } else if (issue === 'phone_not_verified') {
        toast.error('Phiên xác minh OTP đã hết hạn — vui lòng verify lại');
        setOtpVerified(false);
        setOtpRequested(false);
        setStep(2);
      } else if (issue === 'email_not_verified') {
        toast.error('Phiên xác minh email đã hết hạn — vui lòng verify lại');
        setEmailVerified(false);
        setEmailPhase('enter');
        setStep(1);
      } else {
        // `id` cố định: bấm lại nhiều lần thì thay toast cũ, không chồng một cột lỗi giống nhau.
        toast.error(msg || 'Đăng ký thất bại', { id: 'signup-submit' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-purple-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative blobs match brand pink/magenta — subtle, không gây chú ý */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-pink-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="w-full max-w-md relative z-10">
        {/* Brand header — logo + slogan */}
        <div className="text-center mb-8">
          <img src="/cnvwork.png" alt="CNV Work" className="w-20 h-20 mx-auto rounded-2xl shadow-lg mb-4 object-contain" />
          <h1 className="text-2xl font-bold text-slate-900">CNV Work</h1>
          <p className="text-xs font-semibold text-slate-500 mt-1 tracking-widest">
            CRM · OPERATIONS · AI AGENT
          </p>
        </div>

        {/* Progress bar */}
        <div className="flex items-center justify-between mb-8 px-2">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                step > s.num ? 'bg-green-500 text-white' :
                step === s.num ? 'bg-blue-600 text-white' :
                'bg-slate-200 text-slate-500'
              }`}>
                {step > s.num ? <CheckCircle2 size={18} /> : s.num}
              </div>
              <div className="ml-2 flex-1">
                <div className={`text-xs ${step >= s.num ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>{s.label}</div>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 mx-2 ${step > s.num ? 'bg-green-500' : 'bg-slate-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <form onSubmit={step === 3 ? onSubmit : next}>
            {/* Step 1: Email (phase 'enter') */}
            {step === 1 && emailPhase === 'enter' && (
              <>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">Email của anh/chị</h2>
                <p className="text-sm text-slate-500 mb-6">Chúng tôi sẽ gửi mã xác minh để bảo vệ tài khoản</p>
                <div className="relative">
                  <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    autoFocus
                    required
                    value={form.email}
                    onChange={update('email')}
                    maxLength={EMAIL_MAX}
                    placeholder="ban@congty.com"
                    className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                {form.email.length > 0 && !isEmailValid && (
                  <p className="text-xs text-red-600 mt-1.5">
                    {form.email.length > EMAIL_MAX
                      ? `Email vượt quá ${EMAIL_MAX} ký tự (RFC 5321)`
                      : 'Email không đúng định dạng (vd: ban@congty.com)'}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={!isEmailValid || emailOtpRequesting}
                  className="mt-6 w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  Gửi mã xác minh
                  <ArrowRight size={18} />
                </button>
                <div className="text-center text-sm text-slate-500 mt-4">
                  Đã có tài khoản? <Link to="/login" className="text-blue-600 hover:underline font-medium">Đăng nhập</Link>
                </div>
              </>
            )}

            {/* Step 1: Email (phase 'verify') — render ngay khi user bấm "Gửi mã" */}
            {step === 1 && emailPhase === 'verify' && (
              <>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">Nhập mã xác minh</h2>
                <p className="text-sm text-slate-500 mb-1 break-all">
                  Đã gửi đến <span className="font-medium text-slate-700">{form.email}</span>
                </p>
                <div className="text-[11px] mb-5 flex items-center gap-1.5">
                  {emailSendStatus === 'sending' && (
                    <span className="text-slate-400 inline-flex items-center gap-1">
                      <Loader2 size={11} className="animate-spin" /> Đang gửi...
                    </span>
                  )}
                  {emailSendStatus === 'sent' && (
                    <span className="text-green-600 inline-flex items-center gap-1">
                      <CheckCircle2 size={11} /> Đã gửi, kiểm tra hộp thư
                    </span>
                  )}
                  {emailSendStatus === 'failed' && (
                    <span className="text-red-600">Không gửi được — bấm "Gửi lại"</span>
                  )}
                </div>

                <input
                  type="text"
                  autoFocus
                  value={emailOtpCode}
                  onChange={(e) => setEmailOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center tracking-[0.4em] font-mono text-2xl"
                />

                <div className="flex items-center justify-between mt-3 text-xs">
                  <button
                    type="button"
                    onClick={() => requestEmailOtp()}
                    disabled={emailOtpCountdown > 0 || emailOtpRequesting}
                    className="text-blue-600 hover:underline disabled:text-slate-400 disabled:hover:no-underline"
                  >
                    {emailOtpRequesting
                      ? 'Đang gửi...'
                      : emailOtpCountdown > 0
                        ? `Gửi lại sau ${emailOtpCountdown}s`
                        : 'Gửi lại mã'}
                  </button>
                  <span className="text-slate-400">Mã có hiệu lực 10 phút</span>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={back}
                    className="px-4 py-3 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <button
                    type="submit"
                    disabled={!isEmailOtpValid || emailOtpVerifying}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {emailOtpVerifying ? <><Loader2 size={18} className="animate-spin" /> Đang xác minh...</> : <>Xác minh <ArrowRight size={18} /></>}
                  </button>
                </div>
              </>
            )}

            {/* Step 2: Password + Name */}
            {step === 2 && (
              <>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">Tạo mật khẩu</h2>
                <p className="text-sm text-slate-500 mb-6">Cho tài khoản <span className="font-medium text-slate-700">{form.email}</span></p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Họ và tên</label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        autoFocus
                        value={form.display_name}
                        onChange={update('display_name')}
                        placeholder="Nguyễn Văn A"
                        className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">Mật khẩu</label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type={showPwd ? 'text' : 'password'}
                        required
                        value={form.password}
                        onChange={update('password')}
                        placeholder={t`Tối thiểu 10 ký tự`}
                        className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPwd(!showPwd)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {form.password && (
                      <>
                        <div className="flex gap-1 mt-2">
                          {[1, 2, 3, 4].map(n => (
                            <div key={n} className={`h-1 flex-1 rounded ${
                              form.password.length >= n * 3
                                ? (isPwdValid ? 'bg-green-500' : 'bg-amber-400')
                                : 'bg-slate-200'
                            }`} />
                          ))}
                        </div>
                        {pwdTooShort ? (
                          <p className="text-xs text-amber-600 mt-1.5">
                            <Trans>Còn thiếu {PWD_MIN - form.password.length} ký tự — mật khẩu phải từ 10 ký tự</Trans>
                          </p>
                        ) : pwdProblem && (
                          <p className="text-xs text-red-600 mt-1.5">{pwdProblem}</p>
                        )}
                      </>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1.5">
                      Số điện thoại
                      {otpVerified && (
                        <span className="ml-2 inline-flex items-center gap-1 text-green-600 text-[11px] font-semibold">
                          <ShieldCheck size={12} /> Đã xác minh
                        </span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <div className="relative flex-1 min-w-0">
                        <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="tel"
                          required
                          value={form.phone}
                          onChange={update('phone')}
                          disabled={otpVerified}
                          placeholder="0909xxxxxx"
                          inputMode="tel"
                          className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-600"
                        />
                      </div>
                      {!otpVerified && (
                        <button
                          type="button"
                          onClick={requestOtp}
                          disabled={!isPhoneValid || otpRequesting || otpCountdown > 0}
                          className="shrink-0 px-3 py-3 bg-slate-100 hover:bg-slate-200 disabled:bg-slate-50 disabled:text-slate-400 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap transition-colors flex items-center gap-1"
                        >
                          {otpRequesting
                            ? <Loader2 size={16} className="animate-spin" />
                            : otpCountdown > 0
                              ? `${otpCountdown}s`
                              : otpRequested ? 'Gửi lại' : 'Gửi OTP'}
                        </button>
                      )}
                    </div>
                  </div>

                  {otpRequested && !otpVerified && (
                    <div>
                      <label className="block text-xs font-medium text-slate-700 mb-1.5">Mã OTP (6 chữ số)</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={otpCode}
                          onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          maxLength={6}
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          size={6}
                          className="flex-1 min-w-0 px-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center tracking-[0.3em] font-mono text-lg"
                        />
                        <button
                          type="button"
                          onClick={verifyOtp}
                          disabled={!isOtpValid || otpVerifying}
                          className="shrink-0 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-1"
                        >
                          {otpVerifying ? <Loader2 size={16} className="animate-spin" /> : 'Xác minh'}
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-1.5">Mã OTP có hiệu lực trong 5 phút. Kiểm tra tin nhắn SMS.</p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={back}
                    className="px-4 py-3 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <button
                    type="submit"
                    disabled={!isPwdValid || !otpVerified}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    Tiếp tục
                    <ArrowRight size={18} />
                  </button>
                </div>

                {(!isPwdValid || !otpVerified) && (
                  <p className="text-xs text-slate-500 text-center mt-2.5">
                    {pwdTooShort
                      ? <Trans>Nút “Tiếp tục” mở khi mật khẩu đủ 10 ký tự</Trans>
                      : !isPwdValid
                      ? <Trans>Đổi mật khẩu theo lời nhắc dưới ô để mở nút “Tiếp tục”</Trans>
                      : otpRequested
                        ? <Trans>Nhập mã OTP 6 số vừa gửi để mở nút “Tiếp tục”</Trans>
                        : <Trans>Bấm “Gửi OTP” rồi xác minh số điện thoại để mở nút “Tiếp tục”</Trans>}
                  </p>
                )}
              </>
            )}

            {/* Step 3: Org */}
            {step === 3 && (
              <>
                <h2 className="text-xl font-semibold text-slate-900 mb-1">Tên doanh nghiệp</h2>
                <p className="text-sm text-slate-500 mb-6">Đây là workspace chính của anh/chị, có thể đổi sau</p>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">Tên công ty / workspace</label>
                  <div className="relative">
                    <Building2 size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      autoFocus
                      required
                      value={form.org_name}
                      onChange={update('org_name')}
                      placeholder="Công ty TNHH ABC"
                      className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {!isOrgValid && (
                    <p className="text-xs text-slate-500 mt-1.5">
                      {form.org_name.trim().length === 0
                        ? <Trans>Nhập tên doanh nghiệp để mở nút “Tạo workspace”</Trans>
                        : <Trans>Tên doanh nghiệp phải từ 2 ký tự</Trans>}
                    </p>
                  )}
                </div>

                {/* Summary */}
                <div className="mt-4 p-3 bg-slate-50 rounded-lg text-xs space-y-1">
                  <div className="flex justify-between"><span className="text-slate-500">Email:</span><span className="font-medium text-slate-900">{form.email}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Tên hiển thị:</span><span className="font-medium text-slate-900">{form.display_name || form.email.split('@')[0]}</span></div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={back}
                    disabled={submitting}
                    className="px-4 py-3 border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <button
                    type="submit"
                    disabled={!isOrgValid || submitting}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {submitting ? <><Loader2 size={18} className="animate-spin" /> Đang tạo...</> : 'Tạo workspace'}
                  </button>
                </div>

                <p className="text-xs text-slate-400 text-center mt-4">
                  Bằng việc tiếp tục, anh/chị đồng ý với{' '}
                  <a href="#" className="text-blue-600 hover:underline">Điều khoản</a> và{' '}
                  <a href="#" className="text-blue-600 hover:underline">Chính sách bảo mật</a>
                </p>
              </>
            )}
          </form>
        </div>

        <p className="text-center text-xs text-slate-400 mt-4">
          Dùng thử miễn phí 14 ngày — đủ tính năng CNV Work, không giới hạn tài khoản
        </p>
      </div>
    </div>
  );
}
